import React, { useState } from "react";
import {
    alpha,
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Typography,
    useTheme,
} from "@mui/material";
import PersonAddRounded from "@mui/icons-material/PersonAddRounded";
import TextField from "../TextField";
import Button from "../Button";
import { FONTS } from "../../constants/styles";

interface CreateClientModalProps {
    open: boolean;
    onClose: () => void;
    onSubmit: (name: string) => void;
}

const CreateClientModal: React.FC<CreateClientModalProps> = ({
    open,
    onClose,
    onSubmit,
}) => {
    const theme = useTheme();
    const hex = theme.palette.primary.main;

    const [name, setName] = useState("");
    const [error, setError] = useState(false);

    const handleSubmit = () => {
        if (!name.trim()) {
            setError(true);
            return;
        }
        setError(false);
        onSubmit(name.trim());
        setName("");
        onClose();
    };

    const handleClose = () => {
        setError(false);
        setName("");
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            PaperProps={{
                sx: {
                    borderRadius: "20px",
                    p: 3,
                    textAlign: "center",
                    boxShadow: "0px 8px 32px rgba(0, 0, 0, 0.12)",
                    maxWidth: 360,
                    mx: 2,
                },
            }}
            className="force-dynamic-translation"
        >
            <DialogTitle sx={{ p: 0, pb: 0.5 }}>
                <Stack spacing={2} alignItems="center">
                    {/* Tinted icon box */}
                    <Box
                        sx={{
                            width: 48,
                            height: 48,
                            borderRadius: "14px",
                            bgcolor: alpha(hex, 0.1),
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flex: "0 0 auto",
                            color: hex,
                        }}
                    >
                        <PersonAddRounded sx={{ fontSize: 24 }} />
                    </Box>
                    <Typography
                        fontFamily={FONTS.display}
                        fontWeight={700}
                        fontSize="20px"
                    >
                        Create Client
                    </Typography>
                </Stack>
            </DialogTitle>
            <DialogContent sx={{ p: 0, pt: 1 }}>
                <Stack spacing={2}>
                    <Typography
                        fontWeight={400}
                        fontSize="14px"
                        lineHeight="150%"
                        color="text.secondary"
                    >
                        You are creating a new client. They will be able to
                        update their name and provide additional information,
                        including their email, later.
                    </Typography>
                    <TextField
                        fullWidth
                        variant="outlined"
                        value={name}
                        onChange={(e) => {
                            setName(e.target.value);
                            if (error) setError(false);
                        }}
                        onEnter={handleSubmit}
                        placeholder="Enter client's name"
                        maxLength={20}
                        error={error}
                        helperText={error ? "Please enter a valid name" : undefined}
                    />
                </Stack>
            </DialogContent>
            <DialogActions
                sx={{ justifyContent: "center", gap: 1, p: 0, pt: 2.5 }}
            >
                <Button
                    onClick={handleClose}
                    variant="medium"
                    color="secondary"
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="medium"
                    color="primary"
                >
                    Create
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CreateClientModal;
